import store from "../redux/store";
import actions from "../redux/user/actions";

const SESSION_TIMEOUT = parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 900000; // 15 minutes default
const WARNING_TIME = parseInt(process.env.REACT_APP_SESSION_WARNING_TIME) || 60000; // 1 minute default

class SessionTimeoutManager {
  constructor() {
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.lastActivity = Date.now();
    this.warningCallback = null;
    this.logoutCallback = null;
    this.events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
  }

  /**
   * Initialize session timeout tracking
   * @param {Function} onWarning - Callback when warning time is reached
   * @param {Function} onLogout - Callback when session expires
   */
  init(onWarning, onLogout) {
    this.warningCallback = onWarning;
    this.logoutCallback = onLogout;

    // Bind activity listeners
    this.events.forEach((event) => {
      window.addEventListener(event, this.resetTimer.bind(this), true);
    });

    // Start the timer
    this.resetTimer();
  }

  /**
   * Reset the inactivity timer
   */
  resetTimer() {
    this.lastActivity = Date.now();

    // Clear existing timers
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);

    // Set warning timer
    this.warningTimeoutId = setTimeout(() => {
      if (this.warningCallback) {
        this.warningCallback();
      }
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set logout timer
    this.timeoutId = setTimeout(() => {
      this.handleLogout();
    }, SESSION_TIMEOUT);
  }

  /**
   * Handle automatic logout
   */
  handleLogout() {
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();

    // Dispatch logout actions
    store.dispatch(actions.setLoggedIn(false));
    store.dispatch(actions.setUser(null));
    store.dispatch(actions.setToken(null));
    store.dispatch(actions.setRole(null));
    store.dispatch(actions.setPermission([]));
    store.dispatch(actions.setSidebarItems([]));

    // Call logout callback
    if (this.logoutCallback) {
      this.logoutCallback();
    }

    // Redirect to login
    window.location.href = "/admin/login";
  }

  /**
   * Manually extend the session
   */
  extendSession() {
    this.resetTimer();
  }

  /**
   * Get remaining session time in milliseconds
   */
  getRemainingTime() {
    const elapsed = Date.now() - this.lastActivity;
    return Math.max(0, SESSION_TIMEOUT - elapsed);
  }

  /**
   * Cleanup - remove event listeners
   */
  cleanup() {
    this.events.forEach((event) => {
      window.removeEventListener(event, this.resetTimer.bind(this), true);
    });

    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
  }

  /**
   * Check if session is still valid
   */
  isSessionValid() {
    return this.getRemainingTime() > 0;
  }
}

// Export singleton instance
export const sessionTimeout = new SessionTimeoutManager();
export default sessionTimeout;
